/**
 * First-touch attribution capture.
 *
 * Reads UTM parameters and click IDs from the landing URL, stores them in
 * localStorage on the very first visit, and never overwrites them. This is
 * "first-touch" attribution: whichever campaign brought the user to the site
 * gets the credit when they sign up or buy, even if they later return through
 * a different channel.
 *
 * Lifetime: 90 days. Matches Meta's default attribution window.
 *
 * Storage shape (single key, JSON):
 *   {
 *     utm_source, utm_medium, utm_campaign, utm_content, utm_term,
 *     fbclid, gclid,
 *     landing_path, referrer, captured_at
 *   }
 */

const STORAGE_KEY = "wb_attribution";
const TTL_DAYS = 90;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const CLICK_ID_KEYS = ["fbclid", "gclid"] as const;

export type Attribution = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  landing_path: string | null;
  referrer: string | null;
  captured_at: string;
};

const EMPTY: Attribution = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
  fbclid: null,
  gclid: null,
  landing_path: null,
  referrer: null,
  captured_at: "",
};

function isExpired(captured_at: string): boolean {
  if (!captured_at) return true;
  const t = Date.parse(captured_at);
  if (Number.isNaN(t)) return true;
  return Date.now() - t > TTL_MS;
}

function readStored(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Attribution;
    if (isExpired(parsed.captured_at)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Run on app boot. Captures any UTM/click-id params from the URL into
 * localStorage if and only if no fresh attribution is already stored.
 * First-touch wins.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;

  const existing = readStored();
  if (existing) return;

  const params = new URLSearchParams(window.location.search);
  const next: Attribution = { ...EMPTY, captured_at: new Date().toISOString() };

  let hasAny = false;
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) {
      next[key] = v.slice(0, 200);
      hasAny = true;
    }
  }
  for (const key of CLICK_ID_KEYS) {
    const v = params.get(key);
    if (v) {
      next[key] = v.slice(0, 200);
      hasAny = true;
    }
  }

  // Capture landing context regardless of whether UTMs are present, so we
  // still know "first page they landed on" + organic referrer.
  next.landing_path = window.location.pathname + window.location.search;
  next.referrer = document.referrer ? document.referrer.slice(0, 500) : null;

  // Only persist if we actually captured something useful (UTM, click id,
  // or external referrer). Avoids overwriting nothing with nothing on every
  // internal navigation.
  if (!hasAny && !next.referrer) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage disabled / quota exceeded — no-op
  }
}

/**
 * Returns the stored attribution payload, or null if none was captured /
 * the record has expired. Read-only — callers should not mutate.
 */
export function getAttribution(): Attribution | null {
  return readStored();
}

/**
 * Returns the attribution as a plain object suitable for sending to APIs.
 * Empty object if no attribution captured.
 */
export function getAttributionPayload(): Record<string, string> {
  const a = readStored();
  if (!a) return {};
  const out: Record<string, string> = {};
  for (const k of UTM_KEYS) if (a[k]) out[k] = a[k]!;
  for (const k of CLICK_ID_KEYS) if (a[k]) out[k] = a[k]!;
  if (a.landing_path) out.landing_path = a.landing_path;
  if (a.referrer) out.referrer = a.referrer;
  return out;
}

/**
 * Flatten attribution into Stripe metadata-friendly key/value pairs.
 * Stripe metadata: max 50 keys, key <= 40 chars, value <= 500 chars.
 */
export function getStripeMetadata(): Record<string, string> {
  return getAttributionPayload();
}

/** Test helper. Not used in app code. */
export function _clearAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}
